import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext();

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);

    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, []);

    const addNotification = useCallback((message, type = 'info', duration = 5000) => {
        const id = Date.now() + Math.random();
        const notification = {
            id,
            message,
            type, // 'success', 'error', 'warning', 'info'
            duration
        };

        setNotifications(prev => [...prev, notification]);

        // Auto-remove after duration
        if (duration > 0) {
            setTimeout(() => {
                removeNotification(id);
            }, duration);
        }

        return id;
    }, [removeNotification]);

    const success = useCallback((message, duration = 4000) => 
        addNotification(message, 'success', duration), [addNotification]);

    const error = useCallback((message, duration = 6000) => 
        addNotification(message, 'error', duration), [addNotification]);

    const warning = useCallback((message, duration = 5000) => 
        addNotification(message, 'warning', duration), [addNotification]);

    const info = useCallback((message, duration = 4000) => 
        addNotification(message, 'info', duration), [addNotification]);

    const value = {
        notifications,
        addNotification,
        removeNotification,
        success,
        error,
        warning,
        info
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
